import { combineReducers, createReducer } from "@reduxjs/toolkit";

import type {
  AddNodeAction,
  RemoveHandlerAction,
  RemoveNodeAction,
  RemoveAllTriggersAction,
  RemoveTriggerAction,
  ReorderHandlerAction,
  ReorderResponseAction,
  ReorderTriggerAction,
  TriggerDesignsState,
} from "../../../schemas/index.ts";
import { InvalidActionError } from "../../InvalidActionError.ts";
import { addNodeAction, removeNodeAction } from "../actions.ts";
import type { ConditionAction } from "./conditionsReducer.ts";
import { conditionsInitialState, conditionsReducerCreate } from "./conditionsReducer.ts";
import type { EffectAction } from "./effectsReducer.ts";
import { effectsInitialState, effectsReducerCreate } from "./effectsReducer.ts";
import type { HandlerEntryAction } from "./handlerEntriesReducer.ts";
import { handlersByIdReducerCreate } from "./handlerEntriesReducer.ts";
import { getAddNodeDerivedActions } from "./helpers/getAddNodeDerivedActions.ts";
import { validateIds } from "./helpers/validateIds.ts";
import { idsInitialState, idsReducerCreate } from "./idsReducer.ts";
import type { ResponsesAction } from "./responsesReducer.ts";
import { responsesByIdReducer as responsesByIdReducerCreate } from "./responsesReducer.ts";
import {
  removeHandlerAction,
  removeAllTriggersAction,
  removeResponseAction,
  removeTriggerAction,
} from "./triggerActions.ts";
import { triggersByIdReducerCreate, type TriggerEntryAction } from "./triggerEntriesReducer.ts";
import type { TriggersReducerOptions } from "./triggersReducerOptions.ts";
export type { TriggersReducerOptions } from "./triggersReducerOptions.ts";

// NOTE: some node designs actions will be "listened to" e.g.
// when a node is deleted we need to delete any triggers
// and any effects or conditions referencing it
export type TriggersAction =
  | ResponsesAction
  | EffectAction
  | ConditionAction
  | TriggerEntryAction
  | HandlerEntryAction
  | ReorderTriggerAction
  | ReorderHandlerAction
  | ReorderResponseAction
  | RemoveAllTriggersAction
  | AddNodeAction
  | RemoveNodeAction;

export const triggersInitialState: TriggerDesignsState = {
  triggersById: {},
  handlersById: {},
  responsesById: {},
  effects: effectsInitialState,
  conditions: conditionsInitialState,
  ids: idsInitialState,
};

const combinedReducersCreate = ({ notifyEvent, isNodeIdValid }: TriggersReducerOptions) =>
  combineReducers({
    triggersById: triggersByIdReducerCreate({ notifyEvent }),
    handlersById: handlersByIdReducerCreate({ notifyEvent }),
    responsesById: responsesByIdReducerCreate({ notifyEvent }),
    effects: effectsReducerCreate({ notifyEvent, isNodeIdValid }),
    conditions: conditionsReducerCreate({ notifyEvent, isNodeIdValid }),
    ids: idsReducerCreate({ isNodeIdValid }),
  });

export const triggersReducerCreate = (options: TriggersReducerOptions = {}) => {
  const combinedReducer = combinedReducersCreate(options);

  const triggersReducer = createReducer(triggersInitialState, (builder) => {
    builder.addCase(removeNodeAction, (state, action) => {
      const { id: nodeId } = action.payload;
      const triggerIds = state.ids.triggerIdsByNodeId[nodeId] ?? [];
      const derivedActions = triggerIds.map((id): RemoveTriggerAction => removeTriggerAction({ id }));

      return [action].reduce(combinedReducer, derivedActions.reduce(triggersReducer, state));
    });

    builder.addCase(removeAllTriggersAction, (state, action) => {
      const { nodeId } = action.payload;
      const triggerIds = state.ids.triggerIdsByNodeId[nodeId] ?? [];
      const derivedActions = triggerIds.map((id): RemoveTriggerAction => removeTriggerAction({ id }));

      return derivedActions.reduce(triggersReducer, state);
    });

    builder.addCase(removeTriggerAction, (state, action) => {
      const { id: triggerId } = action.payload;
      const handlerIds = state.ids.handlerIdsByTriggerId[triggerId] ?? [];

      const derivedActions = handlerIds.map((id): RemoveHandlerAction => removeHandlerAction({ id }));

      return [action].reduce(combinedReducer, derivedActions.reduce(triggersReducer, state));
    });

    builder.addCase(removeHandlerAction, (state, action) => {
      const { id: handlerId } = action.payload;
      const responseIds = state.ids.responseIdsByHandlerId[handlerId] ?? [];

      const derivedActions = responseIds.map(
        // effects and conditions reducers will respond
        (id) => removeResponseAction({ id }),
      );

      return [action].reduce(combinedReducer, derivedActions.reduce(triggersReducer, state));
    });

    builder.addCase(addNodeAction, (state, action) => {
      const validationResult = validateIds(action.payload.node);
      if (!validationResult.valid) {
        throw new InvalidActionError(action, validationResult.reason);
      }
      const derivedActions = getAddNodeDerivedActions(action);
      if (derivedActions.length === 0) return combinedReducer(state, action);

      // top down instead of bottom up
      return derivedActions.reduce(triggersReducer, [action].reduce(combinedReducer, state));
    });

    builder.addDefaultCase((state, action) => {
      return combinedReducer(state, action as TriggersAction);
    });
  });

  return triggersReducer;
};
